import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';

/**
 * Fractional ordering keys. Items that can be reordered (collections, fields, views, records, polls,
 * boards) carry an `order` string; sorting is plain byte-wise string comparison (never localeCompare).
 * Two peers inserting at the same spot may produce identical keys, so every comparison falls back
 * to the id to stay deterministic across devices.
 */

export interface Ordered {
  id: string;
  order: string;
}

export function compareOrder(a: Ordered, b: Ordered): number {
  if (a.order < b.order) return -1;
  if (a.order > b.order) return 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function sortByOrder<T extends Ordered>(items: Iterable<T>): T[] {
  return Array.from(items).sort(compareOrder);
}

/** A key strictly between `before` and `after` (either may be null for the open end). */
export function orderBetween(before?: string | null, after?: string | null): string {
  const a = before ?? null;
  const b = after ?? null;
  if (a !== null && b !== null && a >= b) {
    // Concurrent edits produced equal or inverted neighbours: append after the lower bound.
    return generateKeyBetween(a > b ? a : b, null);
  }
  return generateKeyBetween(a, b);
}

export function ordersBetween(before: string | null | undefined, after: string | null | undefined, n: number): string[] {
  return generateNKeysBetween(before ?? null, after ?? null, n);
}

/** Key that sorts after every item in `items`. */
export function orderAfterLast(items: readonly Ordered[]): string {
  let max: string | null = null;
  for (const it of items) if (max === null || it.order > max) max = it.order;
  return orderBetween(max, null);
}

/** Key that sorts before every item in `items`. */
export function orderBeforeFirst(items: readonly Ordered[]): string {
  let min: string | null = null;
  for (const it of items) if (min === null || it.order < min) min = it.order;
  return orderBetween(null, min);
}

/**
 * Key for moving an item to `index` within `sorted` (the list *without* the moved item).
 */
export function orderAtIndex(sorted: readonly Ordered[], index: number): string {
  const i = Math.max(0, Math.min(index, sorted.length));
  return orderBetween(sorted[i - 1]?.order ?? null, sorted[i]?.order ?? null);
}
